import {
  Card,
  Skeleton,
  Box,
  Stack,
  Typography,
  Grid,
  Button,
  Divider,
} from '@mui/material';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InfoContainer,
  labelColor,
  TitleInfo,
} from './dashboard/details/Donation';
import { AddRounded, ArrowRightAlt, Create } from '@mui/icons-material';
import { getFoodTypeLabel } from 'src/utils/foodTypes';
import { formatWeight } from 'src/utils/formatter';
import { capitalize } from 'lodash';
import { Link as RouterLink } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Temperature } from 'careit';
import { parseJsonArray } from 'src/utils/json';

const nonFoodTypes = ['non-food', 'compost', 'unusable'];

const normalizeFoodType = (foodType) =>
  foodType.toLowerCase().replaceAll('_', '-');

const calculateFoodTotal = (weights) => {
  return weights.reduce((total, item) => {
    if (!nonFoodTypes.includes(normalizeFoodType(item.food_type))) {
      return total + item.estimated_weight;
    }
    return total;
  }, 0);
};

const calculateNonFoodTotal = (weights) => {
  return weights.reduce((total, item) => {
    if (nonFoodTypes.includes(normalizeFoodType(item.food_type))) {
      return total + item.estimated_weight;
    }
    return total;
  }, 0);
};

const FoodTypeRow = ({ foodType, weight }) => {
  return (
    <Grid container>
      <Grid xs={6} item>
        {foodType}
      </Grid>
      <Grid xs={6} item>
        {weight}
      </Grid>
    </Grid>
  );
};
const CargoSection = ({
  isLoading,
  totalFoodWeight,
  donation,
  foodItems,
  title,
  allRescues,
  isPartial,
  totalTitle,
  singleRescueView,
  allPartialRescuesAreCompleted = false,
}) => {
  const formatTemp = (tempValue, tempUnit = 'f') => {
    return Number.isFinite(tempValue)
      ? new Temperature(tempValue, 'f').into(tempUnit).valueOf()
      : null;
  };
  const { t } = useTranslation('translation');
  const { user } = useAuth();
  const foodTypes = foodItems.map((item) =>
    item.food_type.toLowerCase().replace('_', '-')
  );
  return (
    <Box>
      <InfoContainer loading={isLoading}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color={labelColor}>
              {title}
            </Typography>{' '}
            <Typography fontWeight="bold" variant="body">
              {foodTypes
                .map((type) => {
                  return getFoodTypeLabel(t, type);
                  // return type;
                })
                .join(', ')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.5,
            }}
          >
            <Typography variant="overline" color={labelColor}>
              {totalTitle}
            </Typography>{' '}
            <Typography whiteSpace="nowrap" fontWeight="bold" variant="body">
              {formatWeight(
                totalFoodWeight,
                foodItems[0]?.estimated_weight_unit
              )}
            </Typography>
          </Box>
        </Stack>
      </InfoContainer>
      <Grid container pb={2} mt={2} spacing={3}>
        {isPartial && !singleRescueView
          ? allRescues.map((rescue) => {
              const partialRescues = parseJsonArray(
                rescue.partial_food_types
              ).filter((r) => foodTypes.includes(r.food_type.toLowerCase()));
              const isDonorTeam = user.team_id === donation.donor_team_id;

              const Component = isDonorTeam ? RouterLink : Box;
              const props = isDonorTeam
                ? {
                    to: PATH_DASHBOARD.viewPartialDonation(
                      donation.id,
                      rescue.id
                    ),
                    style: {
                      textDecoration: 'none',
                      color: 'inherit',
                    },
                  }
                : {};
              return (
                <Grid item xs={6} key={rescue.id}>
                  <Component {...props}>
                    <InfoContainer
                      sx={{
                        mt: 0,
                        py: 1,
                        ...(isDonorTeam
                          ? {
                              cursor: 'pointer',
                            }
                          : {}),
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        {rescue.recipient_business_name ||
                          t('donations.unassigned')}

                        {isDonorTeam && <ArrowRightAlt />}
                      </Stack>
                    </InfoContainer>
                  </Component>
                  <Stack
                    sx={{
                      mt: 1,
                      borderRadius: '7px',
                      overflow: 'hidden',
                    }}
                    direction="column"
                  >
                    {partialRescues.map((r, idx, arr) => {
                      const key = `${r.food_type
                        .toLowerCase()
                        .replaceAll('-', '_')}`;
                      const rescuedWeight = rescue[`${key}_weight`];
                      const rescuedWeightUnit = rescue[`${key}_weight_unit`];

                      const estimatedWeight = r.partial_weight_reserved;
                      const estimatedWeightUnit = r.weight_unit;
                      const isRescued = allPartialRescuesAreCompleted;

                      const itemWeight = isRescued
                        ? rescuedWeight
                        : estimatedWeight;

                      const itemWeightUnit = isRescued
                        ? rescuedWeightUnit
                        : estimatedWeightUnit;

                      return (
                        <Fragment key={idx}>
                          <Box
                            sx={{
                              backgroundColor: 'grey.100',
                              px: 2,
                              py: 0.5,
                            }}
                          >
                            <FoodTypeRow
                              foodType={capitalize(r.food_type)}
                              weight={formatWeight(itemWeight, itemWeightUnit)}
                            />

                            {isRescued && (
                              <Grid container>
                                {rescue[`${key}_pickup_temp`] ? (
                                  <Grid item xs={6}>
                                    <Typography variant="body2">
                                      {t('donations.pickup-temp', {
                                        temp: rescue[`${key}_pickup_temp_unit`],
                                      })}
                                    </Typography>
                                    <Typography variant="body2">
                                      {formatTemp(
                                        parseFloat(
                                          rescue[`${key}_pickup_temp`].toFixed(
                                            2
                                          )
                                        ),
                                        rescue[`${key}_pickup_temp_unit`]
                                      )}
                                    </Typography>
                                  </Grid>
                                ) : null}
                                {rescue[`${key}_dropoff_temp`] ? (
                                  <Grid item xs={6}>
                                    <Typography variant="body2">
                                      {t('donations.dropoff-temp', {
                                        temp: rescue[
                                          `${key}_dropoff_temp_unit`
                                        ],
                                      })}
                                    </Typography>
                                    <Typography variant="body2">
                                      {formatTemp(
                                        parseFloat(
                                          rescue[`${key}_dropoff_temp`].toFixed(
                                            2
                                          )
                                        ),
                                        rescue[`${key}_dropoff_temp_unit`]
                                      )}
                                    </Typography>
                                  </Grid>
                                ) : null}
                              </Grid>
                            )}
                          </Box>
                          {idx !== arr.length - 1 && (
                            <Divider
                              sx={{
                                backgrorundColor: 'grey.300',
                              }}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </Stack>
                </Grid>
              );
            })
          : foodItems.map((item, idx) => {
              const key = item.food_type?.toLowerCase()?.replaceAll('-', '_');
              const rescuedWeight = donation?.[`${key}_weight`];
              const rescuedWeightUnit = donation?.[`${key}_weight_unit`];

              const estimatedWeight = item.estimated_weight;
              const estimatedWeightUnit = item.estimated_weight_unit;

              const isRescued = donation.rescue_status === 'COMPLETED';

              const itemWeight = isRescued ? rescuedWeight : estimatedWeight;

              const itemUnit = isRescued
                ? rescuedWeightUnit
                : estimatedWeightUnit;
              return (
                <Grid key={idx} item xs={12} sm={3} md={2}>
                  <Box>
                    <Box>
                      <Typography
                        style={{ display: 'block' }}
                        whiteSpace="wrap"
                        variant="overline"
                        color={labelColor}
                      >
                        {t(
                          `data.food-types.${item?.food_type
                            ?.toLowerCase()
                            ?.replaceAll('_', '-')}`
                        ).toUpperCase()}{' '}
                      </Typography>
                      <Typography whiteSpace="nowrap" variant="body1">
                        {itemWeight === 0
                          ? '-'
                          : formatWeight(itemWeight, itemUnit)}
                      </Typography>

                      {donation.rescue_status == 'COMPLETED' && (
                        <Grid container mt={1}>
                          {donation[`${key}_pickup_temp`] ? (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                {t('donations.pickup-temp', {
                                  temp: donation[`${key}_pickup_temp_unit`],
                                })}
                              </Typography>
                              <Typography variant="body2">
                                {formatTemp(
                                  parseFloat(
                                    donation[`${key}_pickup_temp`].toFixed(2)
                                  ),
                                  donation[`${key}_pickup_temp_unit`]
                                )}
                              </Typography>
                            </Grid>
                          ) : null}
                          {donation[`${key}_dropoff_temp`] ? (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                {t('donations.dropoff-temp', {
                                  temp: donation[`${key}_dropoff_temp_unit`],
                                })}
                              </Typography>
                              <Typography variant="body2">
                                {formatTemp(
                                  parseFloat(
                                    donation[`${key}_dropoff_temp`].toFixed(2)
                                  ),
                                  donation[`${key}_dropoff_temp_unit`]
                                )}
                              </Typography>
                            </Grid>
                          ) : null}
                        </Grid>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
      </Grid>
    </Box>
  );
};

const DonationCargoDetails = ({
  isLoading,
  donation,
  user,
  setShowCompleteModal,
  isDonationEditable,
  isDonorTeam,
  setShowEditConfirmModal,
  handleEditDonationSingleInstance,
  parsedFoodWeightObject,
  canCompleteRescue,
  allRescues = [],
  singleRescueView = false,
  allPartialRescuesAreCompleted = false,
}) => {
  const { t } = useTranslation('translation');

  const foodItems = parsedFoodWeightObject.filter(
    (item) => !nonFoodTypes.includes(normalizeFoodType(item.food_type))
  );
  const foodRescues = allRescues.filter((rescue) => {
    const partialRescues = parseJsonArray(rescue.partial_food_types);
    return partialRescues.some(
      (r) => !nonFoodTypes.includes(normalizeFoodType(r.food_type))
    );
  });

  const nonFoodItems = parsedFoodWeightObject.filter((item) =>
    nonFoodTypes.includes(normalizeFoodType(item.food_type))
  );
  const nonFoodRescues = allRescues.filter((rescue) => {
    const partialRescues = parseJsonArray(rescue.partial_food_types);
    return partialRescues.some((r) =>
      nonFoodTypes.includes(normalizeFoodType(r.food_type))
    );
  });
  const totalFoodWeight = calculateFoodTotal(parsedFoodWeightObject);
  const totalNonFoodWeight = calculateNonFoodTotal(parsedFoodWeightObject);
  const totalWeight = totalFoodWeight + totalNonFoodWeight;

  return (
    <>
      {isLoading ? (
        <Skeleton
          sx={{
            mt: 3,
          }}
          variant="rounded"
          width="100%"
          height={240}
        />
      ) : (
        <Card sx={{ p: 3, mt: 3 }}>
          <Box>
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              alignItems={{
                xs: 'start',
                sm: 'center',
              }}
              gap={1}
              justifyContent="space-between"
            >
              <TitleInfo variant="h5">{t('donations.cargo')}</TitleInfo>
              <Stack
                direction="row"
                sx={{
                  width: '100%',
                }}
                justifyContent={{
                  xs: 'space-between',
                  sm: 'flex-end',
                }}
                alignItems="center"
                gap={1}
              >
                {canCompleteRescue?.(donation, user) && (
                  <Button
                    onClick={() => setShowCompleteModal(true)}
                    variant="text"
                  >
                    <AddRounded />
                    {t('donations.add-weights')}
                  </Button>
                )}

                {(user?.account_type == 'ADMIN' ||
                  user?.account_type == 'TEAM-STAFF') &&
                  isDonationEditable &&
                  isDonorTeam && (
                    <Button
                      onClick={() =>
                        donation?.is_recurring && !user.is_driver
                          ? setShowEditConfirmModal(true)
                          : handleEditDonationSingleInstance()
                      }
                    >
                      <Create
                        sx={{
                          mr: 1,
                        }}
                      />
                      {t('donations.edit-weights')}
                    </Button>
                  )}
              </Stack>
            </Stack>
            <CargoSection
              allPartialRescuesAreCompleted={allPartialRescuesAreCompleted}
              isLoading={isLoading}
              donation={donation}
              foodItems={foodItems}
              title={t('donations.food')}
              totalTitle={t('donations.total-food-weight')}
              allRescues={foodRescues}
              isPartial={donation?.is_partial}
              totalFoodWeight={totalFoodWeight}
              singleRescueView={singleRescueView}
            />

            {nonFoodItems.length > 0 && (
              <CargoSection
                allPartialRescuesAreCompleted={allPartialRescuesAreCompleted}
                isLoading={isLoading}
                donation={donation}
                foodItems={nonFoodItems}
                title={t('donations.other')}
                totalTitle={t('donations.total-other-weight')}
                allRescues={nonFoodRescues}
                isPartial={donation?.is_partial}
                totalFoodWeight={totalNonFoodWeight}
                singleRescueView={singleRescueView}
              />
            )}

            {/* New total weight line */}
            <InfoContainer loading={isLoading}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="overline" color={labelColor}>
                    {t('donations.total-weight')}
                  </Typography>{' '}
                  <Typography
                    whiteSpace="nowrap"
                    fontWeight="bold"
                    variant="body"
                  >
                    {formatWeight(
                      totalWeight,
                      parsedFoodWeightObject[0]?.estimated_weight_unit
                    )}
                  </Typography>
                </Box>
              </Stack>
            </InfoContainer>
          </Box>
        </Card>
      )}
    </>
  );
};

export default DonationCargoDetails;
